import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useOrganization } from './OrganizationContext';

const TimezoneContext = createContext();

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

// Normalize a backend datetime string to UTC.
// Backend stores all datetimes in UTC but often returns naive strings
// (e.g. "2026-03-10T18:17:00") without a 'Z' or offset suffix.
// JavaScript's new Date() parses these as LOCAL time, causing wrong display.
// This helper appends 'Z' to bare ISO strings so they parse as UTC.
export const parseAsUTC = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);
  if (typeof date !== 'string') return null;
  const s = date.trim();
  // Already has timezone indicator (Z, +HH:MM, -HH:MM) → parse as-is
  if (/Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s)) {
    return new Date(s);
  }
  // Naive ISO string → treat as UTC
  return new Date(s + 'Z');
};

export const TimezoneProvider = ({ children }) => {
  const { organization } = useOrganization();
  const [storedTimezone, setTimezoneState] = useState(() => {
    // Try to load from localStorage, default to 'Africa/Nairobi'
    const saved = localStorage.getItem('app_timezone');
    return saved || 'Africa/Nairobi';
  });

  // Organization settings (Admin > Localization) take precedence over the
  // per-browser fallback so all users see the same timezone and clock format.
  const timezone = organization?.timezone || storedTimezone;
  const timeFormat = organization?.time_format || '24h';
  const hour12 = timeFormat === '12h';

  const setTimezone = useCallback((newTimezone) => {
    setTimezoneState(newTimezone);
    localStorage.setItem('app_timezone', newTimezone);
  }, []);

  // Get current offset for the selected timezone
  const getTimezoneOffset = useCallback(() => {
    try {
      const date = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      
      const parts = formatter.formatToParts(date);
      const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
      
      return timeZoneName || timezone;
    } catch (error) {
      console.error('Error getting timezone offset:', error);
      return timezone;
    }
  }, [timezone]);

  // Format date in the selected timezone
  const formatInTimezone = useCallback((date, options = {}) => {
    try {
      const dateObj = parseAsUTC(date);
      
      if (!dateObj || isNaN(dateObj.getTime())) {
        return '—';
      }

      const defaultOptions = {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12
      };

      return dateObj.toLocaleString(undefined, { ...defaultOptions, ...options });
    } catch (error) {
      console.error('Error formatting date in timezone:', error);
      return '—';
    }
  }, [timezone, hour12]);

  // Format date only (no time) in the selected timezone
  const formatDateInTimezone = useCallback((date, options = {}) => {
    try {
      const dateObj = parseAsUTC(date);
      
      if (!dateObj || isNaN(dateObj.getTime())) {
        return '—';
      }

      const defaultOptions = {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      };

      return dateObj.toLocaleDateString(undefined, { ...defaultOptions, ...options });
    } catch (error) {
      console.error('Error formatting date in timezone:', error);
      return '—';
    }
  }, [timezone]);

  // Format time only (no date) in the selected timezone
  const formatTimeInTimezone = useCallback((date, options = {}) => {
    try {
      const dateObj = parseAsUTC(date);
      
      if (!dateObj || isNaN(dateObj.getTime())) {
        return '—';
      }

      const defaultOptions = {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12
      };

      return dateObj.toLocaleTimeString(undefined, { ...defaultOptions, ...options });
    } catch (error) {
      console.error('Error formatting time in timezone:', error);
      return '—';
    }
  }, [timezone, hour12]);

  const value = useMemo(() => ({
    timezone,
    setTimezone,
    getTimezoneOffset,
    parseAsUTC,
    timeFormat,
    hour12,
    formatInTimezone,
    formatDateInTimezone,
    formatTimeInTimezone
  }), [timezone, setTimezone, getTimezoneOffset, timeFormat, hour12, formatInTimezone, formatDateInTimezone, formatTimeInTimezone]);

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};
